import React from 'react';
import {
  HelpCircle,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Info,
  ArrowDown,
} from 'lucide-react';
import { PageHeader } from '../../components/common/PageHeader';

// Mockup §1: How Lead Capture Works. Pure presentation — no backend.

const OLD_WAY_STEPS: Array<{ text: string; error?: boolean }> = [
  { text: 'WordPress Form submitted' },
  { text: '— Zapier ($73/mo) —', error: true },
  { text: 'Zapier sends to HubSpot' },
  { text: 'HubSpot processes lead' },
  { text: '— Zapier again —', error: true },
  { text: 'Zapier round-robin assigns' },
  { text: '— Zapier again —', error: true },
  { text: 'Zapier pushes to MyCRM' },
  { text: 'No analytics. No attribution.', error: true },
];

const NEW_WAY_STEPS: Array<{ text: string; ok?: boolean }> = [
  { text: 'WordPress Form submitted' },
  { text: '— Direct webhook (free) —', ok: true },
  { text: 'Valutten Cloud Function receives lead' },
  { text: 'Auto-attributed (UTM, source, campaign)' },
  { text: 'Round-robin assigns to broker' },
  { text: 'Instant analytics + trail tracking', ok: true },
];

interface FlowRow {
  step: number;
  stage: string;
  description: string;
  tech: string;
}

const FLOW: FlowRow[] = [
  {
    step: 1,
    stage: 'CAPTURE',
    description:
      'Lead submits form on WordPress, Facebook, Google, or Instagram',
    tech: 'Gravity Forms / Meta Lead Ads / Google Ads',
  },
  {
    step: 2,
    stage: 'WEBHOOK',
    description:
      'Form fires webhook directly to Valutten Cloud Function endpoint',
    tech: 'POST /api/leadIngest (or per-platform webhook)',
  },
  {
    step: 3,
    stage: 'PROCESS',
    description:
      'Cloud Function validates, extracts UTMs, stamps source + campaign',
    tech: 'Firebase Gen 2 Function (australia-southeast1)',
  },
  {
    step: 4,
    stage: 'ASSIGN',
    description:
      'Round-robin logic assigns lead to next broker based on office/rules',
    tech: 'Firestore lead_routing_rules + rotation counter',
  },
  {
    step: 5,
    stage: 'STORE',
    description:
      'Lead written to BigQuery (analytics) + Firestore (real-time inbox)',
    tech: 'BigQuery: leads | Firestore: companies/{id}/leads/{id}',
  },
  {
    step: 6,
    stage: 'TRACK',
    description:
      'When deal settles, commission is linked back to original lead source',
    tech: 'JOIN leads.utm_campaign ON raw_commissions.loan_id',
  },
];

const NO_ZAPIER_REASONS: Array<{ title: string; body: string }> = [
  {
    title: 'Zapier adds latency',
    body: 'Webhook is instant (<100ms). Zapier adds 1-15 minute delays on lower tiers.',
  },
  {
    title: 'Zapier adds cost',
    body: '$73.50 USD/month for multi-step Zaps. Our Cloud Function costs $0.001/day.',
  },
  {
    title: 'Zapier is a failure point',
    body: 'If Zapier has an outage, leads stop flowing. Our webhook is on Firebase infrastructure (99.95% SLA).',
  },
  {
    title: "Zapier can't do analytics",
    body: "It routes data but can't cross-reference leads with settlements, commissions, or broker performance.",
  },
  {
    title: 'HubSpot already has lead rotation',
    body: 'HubSpot Sales Hub has built-in owner rotation. Zapier is re-inventing a native feature.',
  },
];

const HUBSPOT_SCENARIOS: Array<{ title: string; body: string }> = [
  {
    title:
      'If you want email nurture campaigns, landing pages, or marketing automation',
    body: 'Keep HubSpot. It is excellent at marketing. Valutten pulls lead data from HubSpot via API.',
  },
  {
    title: 'If you just need lead capture + attribution + analytics',
    body: 'Skip HubSpot entirely. Valutten captures leads directly via webhooks. Zero extra tools.',
  },
  {
    title: 'If you already have HubSpot and want to keep it',
    body: 'Both work together. HubSpot captures leads, Valutten pulls them in daily + receives webhooks for real-time.',
  },
];

const ComparisonCard: React.FC<{
  title: string;
  subtitle: string;
  badge: string;
  badgeVariant: 'error' | 'ok';
  steps: Array<{ text: string; error?: boolean; ok?: boolean }>;
  footer: string;
}> = ({ title, subtitle, badge, badgeVariant, steps, footer }) => {
  const titleColor = badgeVariant === 'error' ? 'text-red-300' : 'text-emerald-300';
  const border = badgeVariant === 'error' ? 'border-red-500/30' : 'border-emerald-500/30';
  const badgeCls =
    badgeVariant === 'error'
      ? 'bg-red-500/15 text-red-300 border-red-500/30'
      : 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30';
  return (
    <div className={`rounded-lg border ${border} bg-gray-800/40 p-5`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className={`text-sm font-bold ${titleColor}`}>{title}</h3>
          <p className="text-[11px] text-gray-500 mt-0.5">{subtitle}</p>
        </div>
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${badgeCls}`}>
          {badge}
        </span>
      </div>
      <ol className="space-y-2">
        {steps.map((s, idx) => (
          <li
            key={idx}
            className={`text-xs leading-relaxed ${
              s.error
                ? 'text-red-300 font-semibold'
                : s.ok
                ? 'text-emerald-300 font-semibold'
                : 'text-gray-300'
            }`}
          >
            {s.text}
          </li>
        ))}
      </ol>
      <p className="text-[11px] text-gray-500 mt-4 italic">{footer}</p>
    </div>
  );
};

const LeadCaptureHowItWorksPage: React.FC = () => {
  return (
    <div className="space-y-6 p-4 md:p-6">
      <PageHeader
        icon={HelpCircle}
        title="How Lead Capture Works"
        subtitle="Every lead source connects directly to Valutten — no middleware, no Zapier, no manual entry"
      />

      {/* Section 1: Old vs New */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ComparisonCard
          title="Old Way (Typical Stack)"
          subtitle="Multiple systems + glue"
          badge="$7,445 + Zapier $900/yr"
          badgeVariant="error"
          steps={OLD_WAY_STEPS}
          footer="4 systems. 3 Zapier steps. $900/yr tool cost. Multiple failure points."
        />
        <ComparisonCard
          title="Valutten Way (Direct)"
          subtitle="Zero middleware"
          badge="$0 extra — included in Lead Capture"
          badgeVariant="ok"
          steps={NEW_WAY_STEPS}
          footer="1 system. Zero middleware. $0 extra. Lead to settlement tracking."
        />
      </div>

      {/* Section 2: Architecture flow */}
      <div className="rounded-lg border border-gray-700 bg-gray-800/40 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-700">
          <h3 className="text-sm font-semibold text-white">
            Lead Flow Architecture
          </h3>
          <p className="text-[11px] text-gray-500 mt-0.5">
            Zero middleware — direct webhook + API
          </p>
        </div>
        <div className="p-5 space-y-2">
          {FLOW.map((row, idx) => (
            <React.Fragment key={row.step}>
              <div className="flex items-center gap-4 rounded-md border border-gray-700/50 bg-gray-900/40 px-4 py-3">
                <div className="w-8 h-8 rounded-full bg-blue-500/20 border border-blue-500/30 inline-flex items-center justify-center text-xs font-bold text-blue-300 flex-shrink-0">
                  {row.step}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-white uppercase tracking-wider">
                    {row.stage}
                  </div>
                  <div className="text-xs text-gray-300 mt-0.5">
                    {row.description}
                  </div>
                  <div className="text-[10px] text-gray-500 font-mono mt-1 truncate">
                    {row.tech}
                  </div>
                </div>
              </div>
              {idx < FLOW.length - 1 && (
                <div className="flex justify-center">
                  <ArrowDown className="w-4 h-4 text-gray-600" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Section 3: Why No Zapier */}
      <div className="rounded-lg border-l-4 border-l-blue-500 border-t border-r border-b border-gray-700 bg-gray-800/40 p-5">
        <div className="flex items-center gap-2 mb-3">
          <XCircle className="w-4 h-4 text-blue-400" />
          <h3 className="text-sm font-semibold text-white">
            Why no Zapier?
          </h3>
        </div>
        <ul className="space-y-3">
          {NO_ZAPIER_REASONS.map((r) => (
            <li key={r.title} className="text-xs">
              <div className="font-semibold text-white">{r.title}</div>
              <div className="text-gray-400 mt-0.5 leading-relaxed">
                {r.body}
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Section 4: HubSpot */}
      <div className="rounded-lg border-l-4 border-l-amber-500 border-t border-r border-b border-gray-700 bg-gray-800/40 p-5">
        <div className="flex items-center gap-2 mb-3">
          <Info className="w-4 h-4 text-amber-400" />
          <h3 className="text-sm font-semibold text-white">
            Is HubSpot still needed?
          </h3>
        </div>
        <ul className="space-y-3">
          {HUBSPOT_SCENARIOS.map((r) => (
            <li key={r.title} className="text-xs">
              <div className="font-semibold text-white flex items-start gap-2">
                {r.title.startsWith('If you want') ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
                )}
                <span>{r.title}</span>
              </div>
              <div className="text-gray-400 mt-0.5 ml-5 leading-relaxed">
                {r.body}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default LeadCaptureHowItWorksPage;
